/**
 * Logger Utility
 * 
 * This utility provides consistent logging across the application.
 * It includes different log levels and integration with external logging services.
 */

import config from '../configs/env';

// Log levels
export const LOG_LEVELS = {
 DEBUG: 0,
 INFO: 1,
 WARN: 2,
 ERROR: 3,
 NONE: 4
};

// Check if debug mode is enabled via localStorage
const isDebugModeEnabled = () => {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem('DEBUG_MODE') === 'true';
};

// Default configuration
const defaultConfig = {
 level: config.isProduction ? LOG_LEVELS.ERROR : LOG_LEVELS.WARN, // Changed from DEBUG to WARN
 // CRITICAL: Disable console logging in production unless DEBUG_MODE is enabled
 enableConsole: !config.isProduction || isDebugModeEnabled(),
 enableRemote: config.isProduction,
 remoteUrl: null,
 batchSize: 10,
 batchInterval: 5000, // 5 seconds
 appName: config.appName,
 appVersion: config.appVersion,
 environment: config.env,
 // New debug flags for specific categories (only enabled in development or with DEBUG_MODE)
 enableWebSocketLogs: (!config.isProduction || isDebugModeEnabled()) && process.env.REACT_APP_DEBUG_WEBSOCKET === 'true',
 enablePerformanceLogs: (!config.isProduction || isDebugModeEnabled()) && process.env.REACT_APP_DEBUG_PERFORMANCE === 'true',
 enableTradingLogs: (!config.isProduction || isDebugModeEnabled()) && process.env.REACT_APP_DEBUG_TRADING === 'true',
 enableReduxLogs: (!config.isProduction || isDebugModeEnabled()) && process.env.REACT_APP_DEBUG_REDUX === 'true',
 enableCacheLogs: (!config.isProduction || isDebugModeEnabled()) && process.env.REACT_APP_DEBUG_CACHE === 'true',
 enableApiLogs: (!config.isProduction || isDebugModeEnabled()) && process.env.REACT_APP_DEBUG_API === 'true'
};

class Logger {
 constructor(customConfig = {}) {
 this.config = { ...defaultConfig, ...customConfig };
 this.logQueue = [];
 this.timer = null;
 
 if (this.config.enableRemote && this.config.batchInterval > 0) {
 this.startBatchProcessing();
 }
 }
 
 /**
 * Start batch processing of logs
 */
 startBatchProcessing() {
 this.timer = setInterval(() => {
 this.flushLogs();
 }, this.config.batchInterval);
 }
 
 /**
 * Stop batch processing of logs
 */
 stopBatchProcessing() {
 if (this.timer) {
 clearInterval(this.timer);
 this.timer = null;
 }
 }
 
 /**
 * Set the log level
 * 
 * @param {number} level - Log level from LOG_LEVELS
 */
 setLevel(level) {
 this.config.level = level;
 }
 
 /**
 * Log a debug message
 * 
 * @param {string} message - Log message
 * @param {Object} data - Additional data to log
 */
 debug(message, data = {}) {
 this.log(LOG_LEVELS.DEBUG, message, data);
 }
 
 /**
 * Log an info message
 * 
 * @param {string} message - Log message
 * @param {Object} data - Additional data to log
 */
 info(message, data = {}) {
 this.log(LOG_LEVELS.INFO, message, data);
 }
 
 /**
 * Log a warning message
 * 
 * @param {string} message - Log message
 * @param {Object} data - Additional data to log
 */
 warn(message, data = {}) {
 this.log(LOG_LEVELS.WARN, message, data);
 }
 
 /**
 * Log an error message
 * 
 * @param {string} message - Log message
 * @param {Object|Error} data - Error object or additional data
 */
 error(message, data = {}) {
 this.log(LOG_LEVELS.ERROR, message, data);
 }
 
 /**
 * Internal log method
 * 
 * @param {number} level - Log level
 * @param {string} message - Log message
 * @param {Object} data - Additional data to log
 */
 log(level, message, data = {}) {
 // Skip if below configured level
 if (level < this.config.level) return;
 
 // Process error objects
 let processedData = data;
 if (data instanceof Error) {
 processedData = {
 name: data.name,
 message: data.message,
 stack: data.stack,
 ...(data.response ? { response: this.sanitizeResponse(data.response) } : {})
 };
 }
 
 const logEntry = {
 timestamp: new Date().toISOString(),
 level: Object.keys(LOG_LEVELS).find(key => LOG_LEVELS[key] === level) || 'INFO',
 message,
 data: processedData,
 app: this.config.appName,
 version: this.config.appVersion,
 environment: this.config.environment,
 url: window.location.href,
 userAgent: navigator.userAgent
 };
 
 // Console logging if enabled
 if (this.config.enableConsole) {
 this.logToConsole(logEntry);
 }
 
 // Remote logging if enabled
 if (this.config.enableRemote) {
 this.queueForRemoteLogging(logEntry);
 }
 }
 
 /**
 * Sanitize API response for logging
 * 
 * @param {Object} response - API response object
 * @returns {Object} Sanitized response
 */
 sanitizeResponse(response) {
 if (!response) return null;
 
 return {
 status: response.status,
 statusText: response.statusText,
 headers: response.headers,
 data: this.sanitizeData(response.data)
 };
 }
 
 /**
 * Sanitize data for logging (remove sensitive information)
 * 
 * @param {Object} data - Data to sanitize
 * @returns {Object} Sanitized data
 */
 sanitizeData(data) {
 if (!data) return null;
 if (typeof data !== 'object') return data;
 
 // Clone the data to avoid modifying the original
 const sanitized = Array.isArray(data) ? [...data] : { ...data };
 
 // List of sensitive fields to redact
 const sensitiveFields = [
 'password', 'token', 'accessToken', 'refreshToken', 'secret',
 'apiKey', 'authorization', 'credential', 'pin', 'otp'
 ];
 
 // Redact sensitive fields
 Object.keys(sanitized).forEach(key => {
 if (sensitiveFields.some(field => key.toLowerCase().includes(field))) {
 sanitized[key] = '[REDACTED]';
 } else if (typeof sanitized[key] === 'object' && sanitized[key] !== null) {
 sanitized[key] = this.sanitizeData(sanitized[key]);
 }
 });
 
 return sanitized;
 }
 
 /**
 * Log to console
 * 
 * @param {Object} logEntry - Log entry
 */
 logToConsole(logEntry) {
 const { level, message, data, timestamp } = logEntry;
 
 switch (level) {
 case 'DEBUG':
 console.debug(`[${timestamp}] DEBUG:`, message, data);
 break;
 case 'INFO':
 console.info(`[${timestamp}] INFO:`, message, data);
 break;
 case 'WARN':
 console.warn(`[${timestamp}] WARN:`, message, data);
 break;
 case 'ERROR':
 console.error(`[${timestamp}] ERROR:`, message, data);
 break;
 default:
 console.log(`[${timestamp}] ${level}:`, message, data);
 }
 }
 
 /**
 * Queue log entry for remote logging
 * 
 * @param {Object} logEntry - Log entry
 */
 queueForRemoteLogging(logEntry) {
 this.logQueue.push(logEntry);
 
 // If we've reached batch size, flush immediately
 if (this.logQueue.length >= this.config.batchSize) {
 this.flushLogs();
 }
 }
 
 /**
 * Flush logs to remote logging service
 */
 async flushLogs() {
 if (this.logQueue.length === 0) return;
 
 if (!this.config.remoteUrl) {
 console.warn('Remote logging enabled but no URL configured');
 this.logQueue = [];
 return;
 }
 
 const logsToSend = [...this.logQueue];
 this.logQueue = [];
 
 try {
 await fetch(this.config.remoteUrl, {
 method: 'POST',
 headers: {
 'Content-Type': 'application/json'
 },
 body: JSON.stringify({
 logs: logsToSend,
 app: this.config.appName,
 version: this.config.appVersion,
 environment: this.config.environment
 }),
 // Use keepalive to ensure the request completes even if the page is unloading
 keepalive: true
 });
 } catch (error) {
 console.error('Failed to send logs to remote server:', error);
 // Re-queue failed logs
 this.logQueue = [...logsToSend, ...this.logQueue];
 }
 }
}

// Create singleton instance
const logger = new Logger({
 remoteUrl: config.logEndpoint,
 level: config.logLevel ? LOG_LEVELS[config.logLevel] : undefined
});

/**
 * Log a debug message
 *
 * @param {string} message - Log message
 * @param {Object} data - Additional data to log
 */
export const logDebug = (message, data) => logger.debug(message, data);

/**
 * Log an info message
 *
 * @param {string} message - Log message
 * @param {Object} data - Additional data to log
 */
export const logInfo = (message, data) => logger.info(message, data);

/**
 * Conditional logging functions for specific categories
 */
export const logWebSocket = (message, data) => {
  if (logger.config.enableWebSocketLogs) {
    logger.debug(`[WebSocket] ${message}`, data);
  }
};

export const logPerformance = (message, data) => {
  if (logger.config.enablePerformanceLogs) {
    logger.debug(`[Performance] ${message}`, data);
  }
};

export const logTrading = (message, data) => {
  if (logger.config.enableTradingLogs) {
    logger.debug(`[Trading] ${message}`, data);
  }
};

export const logRedux = (message, data) => {
  if (logger.config.enableReduxLogs) {
    logger.debug(`[Redux] ${message}`, data);
  }
};

export const logCache = (message, data) => {
  if (logger.config.enableCacheLogs) {
    logger.debug(`[Cache] ${message}`, data);
  }
};

export const logApi = (message, data) => {
  if (logger.config.enableApiLogs) {
    logger.debug(`[API] ${message}`, data);
  }
};

/**
 * Log a warning message
 * 
 * @param {string} message - Log message
 * @param {Object} data - Additional data to log
 */
export const logWarning = (message, data) => logger.warn(message, data);

/**
 * Log an error message
 * 
 * @param {string} message - Log message
 * @param {Object|Error} data - Error object or additional data
 */
export const logError = (message, data) => logger.error(message, data);

/**
 * Set the log level
 * 
 * @param {string|number} level - Log level name or number
 */
export const setLogLevel = (level) => {
 if (typeof level === 'string') {
 logger.setLevel(LOG_LEVELS[level] || LOG_LEVELS.INFO);
 } else {
 logger.setLevel(level);
 }
};

/**
 * Disable console.log in production to improve performance
 * This prevents excessive logging from blocking the main thread
 *
 * Also provides smart filtering in development/staging based on debug flags
 */
export const disableConsoleInProduction = () => {
  // Save original console methods
  const originalConsole = {
    log: console.log,
    debug: console.debug,
    info: console.info,
    warn: console.warn,
    error: console.error
  };

  // In production, disable all console methods except error (unless debug mode is enabled)
  if (config.isProduction && !isDebugModeEnabled()) {
    // Replace console methods with no-ops (except error)
    console.log = () => {};
    console.debug = () => {};
    console.info = () => {};
    console.warn = () => {};
    // Keep console.error for critical errors

    // Expose method to re-enable console for debugging
    window.enableConsole = () => {
      console.log = originalConsole.log;
      console.debug = originalConsole.debug;
      console.info = originalConsole.info;
      console.warn = originalConsole.warn;
      console.error = originalConsole.error;
      console.log('✅ Console logging re-enabled');
    };

    // Expose method to enable debug mode
    window.enableDebugMode = () => {
      localStorage.setItem('DEBUG_MODE', 'true');
      window.location.reload();
    };

    console.error('ℹ️ Console logging disabled in production. Use window.enableDebugMode() to enable.');
  }
  // In development/staging, filter console.log based on debug flags
  else {
    // Create a smart console.log wrapper that filters based on message content
    console.log = (...args) => {
      const message = args.join(' ').toLowerCase();

      // Check if message contains category-specific keywords
      const isWebSocketLog = message.includes('websocket') || message.includes('socket') || message.includes('ws:') || message.includes('🔌') || message.includes('connected to') || message.includes('disconnected from');
      const isPerformanceLog = message.includes('performance') || message.includes('render') || message.includes('ms') || message.includes('⏱️');
      const isTradingLog = message.includes('trading') || message.includes('order') || message.includes('trade') || message.includes('💰');
      const isReduxLog = message.includes('redux') || message.includes('action') || message.includes('dispatch') || message.includes('store');
      const isCacheLog = message.includes('cache') || message.includes('invalidat') || message.includes('refetch') || message.includes('🔄');
      const isApiLog = message.includes('api') || message.includes('request') || message.includes('response') || message.includes('fetch');

      // Only log if the corresponding debug flag is enabled OR if it's a general log (not category-specific)
      const shouldLog =
        (!isWebSocketLog && !isPerformanceLog && !isTradingLog && !isReduxLog && !isCacheLog && !isApiLog) || // General logs always show
        (isWebSocketLog && logger.config.enableWebSocketLogs) ||
        (isPerformanceLog && logger.config.enablePerformanceLogs) ||
        (isTradingLog && logger.config.enableTradingLogs) ||
        (isReduxLog && logger.config.enableReduxLogs) ||
        (isCacheLog && logger.config.enableCacheLogs) ||
        (isApiLog && logger.config.enableApiLogs);

      if (shouldLog) {
        originalConsole.log(...args);
      }
    };

    // Filter console.debug similarly
    console.debug = (...args) => {
      const message = args.join(' ').toLowerCase();
      const isDebugLog = message.includes('[debug]') || message.includes('debug:');

      // Only show debug logs if at least one debug flag is enabled
      const anyDebugEnabled =
        logger.config.enableWebSocketLogs ||
        logger.config.enablePerformanceLogs ||
        logger.config.enableTradingLogs ||
        logger.config.enableReduxLogs ||
        logger.config.enableCacheLogs ||
        logger.config.enableApiLogs;

      if (anyDebugEnabled || !isDebugLog) {
        originalConsole.debug(...args);
      }
    };

    // Keep info, warn, and error as-is in development/staging
    console.info = originalConsole.info;
    console.warn = originalConsole.warn;
    console.error = originalConsole.error;

    // Expose method to re-enable all console logging
    window.enableAllConsole = () => {
      console.log = originalConsole.log;
      console.debug = originalConsole.debug;
      console.info = originalConsole.info;
      console.warn = originalConsole.warn;
      console.error = originalConsole.error;
      originalConsole.log('✅ All console logging re-enabled (filters removed)');
    };

    // Expose method to disable all console logging
    window.disableAllConsole = () => {
      console.log = () => {};
      console.debug = () => {};
      console.info = () => {};
      console.warn = () => {};
      // Keep error
      originalConsole.error('ℹ️ All console logging disabled. Use window.enableAllConsole() to re-enable.');
    };

    // Log current debug configuration
    const enabledCategories = [];
    if (logger.config.enableWebSocketLogs) enabledCategories.push('WebSocket');
    if (logger.config.enablePerformanceLogs) enabledCategories.push('Performance');
    if (logger.config.enableTradingLogs) enabledCategories.push('Trading');
    if (logger.config.enableReduxLogs) enabledCategories.push('Redux');
    if (logger.config.enableCacheLogs) enabledCategories.push('Cache');
    if (logger.config.enableApiLogs) enabledCategories.push('API');

    if (enabledCategories.length > 0) {
      originalConsole.info(`🔍 Debug logging enabled for: ${enabledCategories.join(', ')}`);
    } else {
      originalConsole.info('ℹ️ All debug categories disabled. Only general logs will show. Use window.enableAllConsole() to see all logs.');
    }
  }
};

// Auto-disable console in production
if (typeof window !== 'undefined') {
  disableConsoleInProduction();
}

export default {
 debug: logDebug,
 info: logInfo,
 warn: logWarning,
 error: logError,
 setLevel: setLogLevel,
 disableConsoleInProduction,
 LOG_LEVELS
};